import React, { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import { Box, Grid, Backdrop } from "@material-ui/core";
import { fetchWrapper } from "src/api-client/fetchWrapper";
import { addNotificationAction } from "src/app-redux/settings/settingsSlice";
import { ax } from "src/common/api/apiClient";
import { IApi } from "src/common/typings/app";
import Heading from "./Heading";
import { InputFields, IForm } from "./InputFields";
import { useStyles } from "./styles";
import CommonLoader from "src/components/Loader/CommonLoader";

/** Backend API response shape for consignee (list returns `address`, not consigneeAddress) */
export interface IConsigneeApiItem {
  id: number;
  address: string | null;
  isActive: boolean;
  name: string | null;
  ntnNumber: string;
  terminalId: number;
}

type Props = {
  children?: React.ReactNode;
  index: any;
  value: any;
};

export function Consignee({ value, index, ...other }: Props): React.ReactElement {
  const dispatch = useDispatch();
  const classes = useStyles();
  const [loader, setLoader] = useState(false);
  const [tableRecord, settableRecord] = React.useState<any[]>([]);
  const pageHeading = "Consignee";
  const [loading, setLoading] = React.useState(false);

  /** Normalize address from API: may be string, different keys, or nested object. */
  const normalizeAddress = (item: any): string => {
    const toStr = (val: unknown): string => {
      if (val == null) return "";
      if (typeof val === "string") return val.trim();
      if (typeof val === "object") {
        const v =
          (val as any).value ??
          (val as any).Value ??
          (val as any).name ??
          (val as any).Name ??
          (val as any).text ??
          (val as any).address ??
          (val as any).Address;
        return typeof v === "string" ? v.trim() : "";
      }
      return String(val).trim();
    };
    // API list returns lowercase "address"; support other variants for consistency
    const keys = [
      "address",
      "Address",
      "consigneeAddress",
      "ConsigneeAddress",
      "addressDetails",
      "AddressDetails",
    ];
    for (const key of keys) {
      const val = item?.[key];
      const s = toStr(val);
      if (s !== "") return s;
    }
    // Fallback: any key containing "address" (API may use different naming)
    if (item && typeof item === "object") {
      for (const key of Object.keys(item)) {
        if (/address/i.test(key)) {
          const s = toStr(item[key]);
          if (s !== "") return s;
        }
      }
    }
    return "";
  };

  /** Map backend fields to table row. API uses "address" (can be null); we always output a string for UI. */
  const mapApiConsigneeToTable = (item: IConsigneeApiItem | any) => {
    const name = item.name ?? item.Name ?? "";
    const consignee = String(name).trim() || "—";
    const address = normalizeAddress(item);
    return {
      id: item.id ?? item.Id,
      consignee,
      ntnNumber: item.ntnNumber ?? item.NtnNumber ?? "",
      consigneeAddress: typeof address === "string" ? address : "",
      isActive: item.isActive ?? item.IsActive ?? false,
    };
  };

  const getListing = async () => {
    let results: any = [];
    try {
      setLoader(true);
      results = await fetchWrapper.get(`get_all-consignee-details?isActive=${false}`);
      if (results.isSuccess == true) {
        const raw = results.value?.consigneeList ?? results.value;
        const list = Array.isArray(raw) ? raw.map(mapApiConsigneeToTable) : [];
        settableRecord(list);
      }
      setLoader(false);
      return;
    } catch (error: any) {
      setLoader(false);
      dispatch(
        addNotificationAction({
          message: error.message,
          options: { variant: "error" },
        })
      );
    }
  };

  useEffect(() => {
    if (value === index) getListing();
  }, [value, index]);

  /** Build payload matching backend: name, ntnNumber, address, isActive, terminalId (insert), id (update). No nulls to avoid .NET "Object reference not set". */
  const formToApiPayload = (formData: IForm, forInsert = false) => {
    const payload: Record<string, unknown> = {
      name: formData.consignee ?? "",
      ntnNumber: formData.ntnNumber ?? "",
      consigneeAddress: formData.consigneeAddress ?? "",
      isActive: Boolean(formData.isActive),
    };
    if (!forInsert) payload.id = formData.id;
    if (forInsert) {
      const terminalId = Number(localStorage.getItem("terminalId"));
      payload.terminalId = Number.isNaN(terminalId) ? 0 : terminalId;
    }
    return payload;
  };

  const updateSubmit = async (formData: IForm) => {
    try {
      setLoading(true);
      const payload = formToApiPayload(formData);
      const res = await ax.put<IApi>("/update-consignee-details", payload);
      if (res.data.isSuccess === true) {
        setLoading(false);
        const tempArr = tableRecord.map((x) =>
          x.id === formData.id
            ? {
                ...x,
                isActive: formData.isActive,
                consignee: formData.consignee,
                ntnNumber: formData.ntnNumber,
                consigneeAddress: formData.consigneeAddress,
              }
            : x
        );
        settableRecord(tempArr);
      } else {
        throw new Error(res.data.error || res.statusText);
      }
    } catch (err) {
      setLoading(false);
    }
  };

  const handleSubmit = async (formData: IForm) => {
    setLoading(true);
    const payload = formToApiPayload(formData, true);
    try {
      const res = await ax.post<IApi>("/insert-consignee-details", payload);
      if (res.data.isSuccess === true) {
        setLoading(false);
        const newId = (res.data as any).value?.id ?? (res.data as any).value?.Id;
        const newRow = {
          id: newId ?? Date.now(),
          consignee: formData.consignee ?? "",
          ntnNumber: formData.ntnNumber ?? "",
          consigneeAddress: formData.consigneeAddress ?? "",
          isActive: formData.isActive ?? false,
        };
        settableRecord((prev) => [newRow, ...prev]);
        // Do not refetch here: getListing() replaces the table and can drop addresses
        // or return a different set (e.g. isActive=false only). New row is already in state.
      } else {
        const errMsg =
          (Array.isArray((res.data as any).errors)
            ? (res.data as any).errors.join(" ")
            : (res.data as any).errors) ??
          res.data.error ??
          res.statusText;
        throw new Error(errMsg);
      }
    } catch (err: any) {
      setLoading(false);
      const message = err?.response?.data?.errors
        ? Array.isArray(err.response.data.errors)
          ? err.response.data.errors.join(" ")
          : err.response.data.errors
        : err?.message;
      if (message)
        dispatch(
          addNotificationAction({
            message: String(message),
            options: { variant: "error" },
          })
        );
    }
  };

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`vertical-tabpanel-${index}`}
      aria-labelledby={`vertical-tab-${index}`}
      {...other}
    >
      {value === index && (
        <>
          <Backdrop className={classes.backdrop} open={loader}>
            <CommonLoader />
          </Backdrop>
          <Box borderColor="grey.200">
            <Heading heading={pageHeading} />
            <Box borderTop={2} mb={1} borderColor="grey.200"></Box>
            <Grid container direction="row">
              <Grid item xs={12} md={12}>
                <InputFields
                  updateSubmit={updateSubmit}
                  handleSubmit={handleSubmit}
                  tableRecord={tableRecord || []}
                  loading={loading}
                />
              </Grid>
            </Grid>
          </Box>
        </>
      )}
    </div>
  );
}
